import React, { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { getOverlay } from './overlays';
import KioskMode from './kiosk_mode';
import Header from './header';
import Loading from './loading';
import DateLabel from './date_label';
import Footer from './footer';
import LatestEvent from './latest_event';
import EventDropdowns from './event_dropdowns';
import PoIDropdowns from './poi_dropdowns';
import GmapDropdowns from './gmap_dropdowns';
import SideMenu from './side_menu';
import MobileDetailOverlay from './mobile_detail_overlay';
import Modal from './modal';
import VideoControls from './video_controls';
import DataReadout from './data_readout';
import BackButton from './back_button';
import GeologyMapLegend from './geology_map_legend'
import ColorBar from './color_bar';
import SpacecraftWithInstruments from './spacecraft_with_instruments';
import '../../www/assets/css/legend.css';

import { eventsStore, poiStore, gmapStore, refsStore, videoStore, datasetStore, kioskStore } from '../managers/globalState';

const isIphone = navigator.userAgent.match(/iPhone/i);

/**
 * This component is dedicated to organizing the UI into it's various sections.
 * To reduce and simplify the code, functional components and react hooks:
 * https://reactjs.org/docs/hooks-reference.html
 */

const spacecraftWithInstrumentsList = [
	{
		scName: 'sc_iss',
		instruments: [{ name: 'sc_iss_oco_3', stylizedInstrumentName: 'OCO3' }, { name: 'sc_iss_ecostress', stylizedInstrumentName: 'Ecostress' }, { name: 'sc_iss_emit', stylizedInstrumentName: 'EMIT' }]
	}
];

export default function UILayout({
	isMobile,
	videoIdPoI,
	videoIdGmap,
	hideUI,

	refs,

	commonProps,
	dataProps,
	headerProps,
	eventProps,
	poiProps,
	gmapProps,
	missionsOverlayProps,
	vitalsOverlayProps,
	detailOverlayProps,
	sideMenuProps,
	colorBarProps,
	modalProps,
	footerProps
}) {
	const { view, previousView, vitalsign, spacecraft } = dataProps;
	const { _loadIconRef, _detailOverlayRef } = refs;

	// Get the showOnGlobe and get ref global states
	const { showOnGlobe } = useSnapshot(eventsStore.state);
	const { showOnGlobePoI } = useSnapshot(poiStore.state);
	const { showOnGlobeGmap } = useSnapshot(gmapStore.state);
	const { get } = useSnapshot(refsStore.state);
	const { videoId } = useSnapshot(videoStore.state);
	const { currentDataset, hasAnimation } = useSnapshot(datasetStore.state);
	const { kioskModeOn } = useSnapshot(kioskStore.state);

	const videoManager = get('video');
	const { latestEvents } = get('content');
	const { externalId } = currentDataset || {};

	const videoType = videoManager.getVideoType(videoId);
	let videoColorBarSrc = null;

	let videoAnimating = false;
	if (videoType === 'global' || videoType === 'geoLocated') {
		videoColorBarSrc = videoManager.getVideo(videoId)?.colorBarSrc[0];
		videoAnimating = true;
	}

	// overlayType state, close callback, and props
	const [overlayType, setOverlayTypeRaw] = useState(null);

	// previousOverlayType helps us determine whether we returned from an overlay or not
	// this is useful when deciding whether ot not to show the back button on an item's detail
	const [previousOverlayType, setPreviousOverlayType] = useState(null);

	// wrap setOverlayTypeRaw to store previousOverlayType when it changes
	const setOverlayType = (type, closedWithSelection) => {
		setPreviousOverlayType(closedWithSelection === true ? overlayType : null);
		setOverlayTypeRaw(type);
	};

	// when closedWithSelection is true, it means the user didn't close the overlay by clicking its X
	const closeOverlay = closedWithSelection => {
		setOverlayType(null, closedWithSelection);
	};

	const [isDetailPanelOpen, setDetailPanelOpenState] = useState(false);

	// If we return to the vitalsign view, we reset the previousOverlayType, except when we're
	// viewing all events on the globe
	useEffect(() => {
		if (view === 'vitalsign' && !showOnGlobe) { setPreviousOverlayType(null); }
	}, [view, showOnGlobe]);
	useEffect(() => {
		if (view === 'vitalsign' && !showOnGlobePoI) { setPreviousOverlayType(null); }
	}, [view, showOnGlobePoI]);
	useEffect(() => {
		if (view === 'vitalsign' && !showOnGlobeGmap) { setPreviousOverlayType(null); }
	}, [view, showOnGlobeGmap]);


	const getOverlayData = () => {
		const data = {
			events: eventProps,
			poi: poiProps,
			gmap: gmapProps,
			vitals: vitalsOverlayProps,
			missions: missionsOverlayProps
		};

		return data[overlayType] || {};
	};

	const overlayProps = {
		scene: commonProps.scene,
		pioneer: commonProps.pioneer,
		closeOverlay,
		setDetailPanelState: panelOpenState => { setDetailPanelOpenState(panelOpenState); },
		data: getOverlayData(),
		isMobile: commonProps.isMobile,
		isWindows: commonProps.isWindows
	};

	const detailPanelStateProps = {
		isPanelOpen: isDetailPanelOpen,
		setPanelOpenState: panelState => { setDetailPanelOpenState(panelState); }
	};

	const videoProps = {
		isMobile: commonProps.isMobile,
		videoId,
		videoIdPoI,
		videoIdGmap,
		get: commonProps.get
	};

	footerProps.showOnGlobe = showOnGlobe;
	footerProps.showOnGlobePoI = showOnGlobePoI;
	footerProps.showOnGlobeGmap = showOnGlobeGmap;

	const classes = `ui${hasAnimation ? ' animating' : ''}${isIphone ? ' move-up' : ''}${videoAnimating ? ' video-animating' : ''}`;

	const latestEvent = Array.isArray(latestEvents) && latestEvents.length ? latestEvents[0] : null;

	const showLatestEvent = latestEvent && view === 'vitalsign' && ['satellites', 'visible_earth'].includes(vitalsign);	// const latestEvent = eventProps.allEvents.length ? eventProps.allEvents[0] : null;
	// The back button should only show for an item's detail if the user click a spacecraft or an event (but not
	// from all events) from the globe itself. Navigating to a spacecraft or event from the overlays does not result
	// in a detail with the back button.
  // MODIFIED in AR version
  // removed overlay since it never takes you back to that anyway AND we want it to show up and go back to the previous vitalsign iff that exists -RK
  // makes back button fixed when clicks at any event or poi and also in navbar -Zahrah
	const showBackButton = (view === 'spacecraft' || view === 'event' || view === 'poi' || view === 'gmap'); 
	// Spacecraft with instruments - ISS instruments OCO-3, Ecostress, EMIT
	const scWithInstrumentsIndex = spacecraftWithInstrumentsList.findIndex(({ scName }) => {return spacecraft ?  spacecraft.includes(scName) : false});

	// Determine doublebar class.
	const doubleBar = (externalId === 'smapSmSalinity8Day')
		|| (externalId === 'precipitationToday')
		|| (videoColorBarSrc === 'ar_liquid_frozen_bar.png');

	return (
		<>
			<div id='UI' className={classes}>
				{hideUI !== true && <Header {...commonProps} {...headerProps} setOverlayType={setOverlayType} />}


				{ !videoId && showLatestEvent && <LatestEvent {...latestEvent} /> }

				<Loading ref={_loadIconRef} />

				{ videoId && <VideoControls {...videoProps} setOverlayType={setOverlayType} detailOverlayRef={_detailOverlayRef} /> }

				<DataReadout {...commonProps} />

				{
					isMobile
						? (
							<>
								<MobileDetailOverlay {...commonProps} {...dataProps} {...detailOverlayProps} {...detailPanelStateProps} />
								{ ((view !== 'event' && !showOnGlobe) || (view !== 'poi' && !showOnGlobePoI) || (view !== 'gmap' && !showOnGlobeGmap))  && <SideMenu {...sideMenuProps} /> }
								{ showOnGlobe && <EventDropdowns /> }
                { showOnGlobePoI && <PoIDropdowns /> }
				{ showOnGlobeGmap && <GmapDropdowns /> }
							</>
						) :
						(
							(showOnGlobe && view !== 'event') ?
								(<EventDropdowns />) :
								(<div id='date-label-container' className={`${doubleBar ? 'double-bar' : ''} showUI`}><DateLabel {...dataProps} /></div>)
						)
				}

				{ showBackButton && <BackButton /> }
				{ footerProps.dataProps.poi.legend && 	<GeologyMapLegend  legend={ footerProps.dataProps.poi.legend }/> }
				{ footerProps.dataProps.gmap.legend && 	<GeologyMapLegend  legend={ footerProps.dataProps.gmap.legend }/> }

				<div id='legend-container' className='legend-container'>
					<ColorBar />
				</div>

				{
					scWithInstrumentsIndex !== -1
				&& (
					<SpacecraftWithInstruments
						spacecraft={spacecraft}
						instrumentData={spacecraftWithInstrumentsList[scWithInstrumentsIndex].instruments}
						pioneer={commonProps.pioneer}
					/>
				)

				}

				<Modal />
				{hideUI !== true && <Footer {...footerProps} {...detailPanelStateProps} />}

				{ overlayType && getOverlay(overlayType, overlayProps) }

			</div>
			{kioskModeOn && <KioskMode {...overlayProps} />}
		</>

	);
}
